import { useFormChangeWarning } from "@/hooks/useFormChangeWarning";
import React, { forwardRef, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

export interface FormRenderRef {
  submitForm: () => void;
  setValue: ReturnType<typeof useForm>["setValue"];
}

interface RenderFormWithRefProps {
  handleFormSubmit: (data: any) => void;
  className?: string;
  formClassName?: string;
  setWatchField?: (data: any) => void;
  watchFieldList?: string[];
  children: React.ReactNode;
}

const RenderFormWithRefAndChildren = forwardRef<FormRenderRef, RenderFormWithRefProps>(
  ({ handleFormSubmit, className = "p-6", formClassName = "", children, setWatchField, watchFieldList }, ref) => {
    const methods = useForm({ mode: "all", reValidateMode: "onChange" });
    const {
      handleSubmit,
      watch,
      setValue,
      formState: { isDirty },
    } = methods;
    useFormChangeWarning(isDirty);
    // Expose submit function to parent
    React.useImperativeHandle(ref, () => ({
      submitForm: () => {
        handleSubmit(handleFormSubmit)();
      },
      setValue, // expose setValue function
    }));

    useEffect(() => {
      if (!watchFieldList?.length) return;
      const formValues = watch();
      const subscription = watch((data, { name }) => {
        if (name && watchFieldList.includes(name)) {
          const watchedValues = watchFieldList.reduce((acc, fieldName) => {
            acc[fieldName] = data[fieldName];
            return acc;
          }, {} as Record<string, any>);
          setWatchField?.(watchedValues);
        }
      });
      const initialWatchedValues = watchFieldList.reduce((acc, fieldName) => {
        acc[fieldName] = formValues[fieldName];
        return acc;
      }, {} as Record<string, any>);
      setWatchField?.(initialWatchedValues);
      return () => subscription.unsubscribe();
    }, [watch]);

    return (
      <div className={className}>
        <FormProvider {...methods}>
          <form className={`flex flex-col w-full gap-y-6 ${formClassName}`} onSubmit={(e) => e.preventDefault()}>
            {children}
          </form>
        </FormProvider>
      </div>
    );
  }
);

RenderFormWithRefAndChildren.displayName = "RenderFormWithRefAndChildren";

export default RenderFormWithRefAndChildren;
